import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  Button,
  Grid,
  Switch,
  FormControlLabel,
  Typography,
  MenuItem
} from "@mui/material";
import { authenticationService } from "src/services";
import { get, put } from "src/services/httpClient.service";
import { useSnackbar } from "src/components/snackbar/useSnackbar";

/* =========================
   Types
========================= */
interface Country {
  id: number;
  name: string;
}

interface UpdateStateProps {
  handleClose: () => void;
  stateidvalues: any;
  fetchStates: () => void;
}

const UpdateState: React.FC<UpdateStateProps> = ({
  handleClose,
  stateidvalues,
  fetchStates
}) => {
  const { vendorId } = authenticationService.getVendorFranchiseIds();
  const APIURL = process.env.REACT_APP_BASE_API_URL;
  const { showSnackbar } = useSnackbar();

  const [countries, setCountries] = useState<Country[]>([]);
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    id: 0,
    countryId: "",
    name: "",
    shortCode: "",
    isActive: true,
    vendorId: vendorId
  });

  /* =========================
     Fetch Countries
  ========================= */
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const res = await get(
          `${APIURL}/api/miscellaneous/country/vendor/${vendorId}/findAll`
        );
        setCountries(res?.data || []);
        console.log(res);
        if(res?.statusCode == '200'){
            //showSnackbar(res?.message, "success");
            fetchStates()
        }else{
            showSnackbar(res?.message, "error");
        }
        
      } catch (error) {
        showSnackbar(error?.message || "something went wrong", "error");
      }
    };

    fetchCountries();
  }, [APIURL, vendorId]);

  /* =========================
     Prefill State Data
  ========================= */
  useEffect(() => {
    if (stateidvalues) {
      setForm({
        id: stateidvalues.id,
        countryId: stateidvalues.countryId,
        name: stateidvalues.name || "",
        shortCode: stateidvalues.shortCode || "",
        isActive: stateidvalues.isActive,
        vendorId: vendorId
      });
    }
  }, [stateidvalues, vendorId]);

  /* =========================
     Handlers
  ========================= */
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (!form.countryId || !form.name) {
      showSnackbar("Country and State Name are required", "warning");
      return;
    }

    setLoading(true);

    try {
      const payload = {
        id: form.id,
        countryId: Number(form.countryId),
        name: form.name,
        shortCode: form.shortCode || null,
        isActive: form.isActive,
        vendorId: form.vendorId
      };

      const response = await put(
        `${APIURL}/api/miscellaneous/state`,
        payload
      );

      if (response?.statusCode === 200) {
        showSnackbar(response?.message || "State updated successfully", "success");
        fetchStates();
        handleClose();
      } else {
        showSnackbar(response?.message || "Failed to update state", "error");
      }
    } catch (error: any) {
      showSnackbar(error?.message || "Failed to update state", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box p={3}>
      <Typography variant="h6" mb={2}>
        Update State
      </Typography>

      <Grid container spacing={2}>
        {/* Country Dropdown */}
        <Grid item xs={12}>
          <TextField
            select
            fullWidth
            required
            label="Country"
            name="countryId"
            value={form.countryId}
            onChange={handleChange}
          >
            {countries.map(country => (
              <MenuItem key={country.id} value={country.id}>
                {country.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        {/* State Name */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            required
            label="State Name"
            name="name"
            value={form.name}
            onChange={handleChange}
          />
        </Grid>

        {/* Short Code */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Short Code (Optional)"
            name="shortCode"
            value={form.shortCode}
            onChange={handleChange}
          />
        </Grid>

        {/* Active Switch */}
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={form.isActive}
                onChange={e =>
                  setForm(prev => ({
                    ...prev,
                    isActive: e.target.checked
                  }))
                }
              />
            }
            label="Is Active"
          />
        </Grid>

        {/* Actions */}
        <Grid item xs={12} display="flex" justifyContent="flex-end" gap={2}>
          <Button variant="outlined" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
          >
            Update
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default UpdateState;
